/**
 * AI Chat Core Utilities
 *
 * Shared utility functions for AG-UI chat features.
 */

/**
 * Generate unique run ID for each AG-UI request
 */
export const generateRunId = (): string => crypto.randomUUID();

/**
 * Parse namespace to extract entity type and ID
 * Format: "type:id" (e.g., "section:uuid-123" or "rule:uuid-456")
 *
 * @param namespace - The namespace string from SSE event
 * @returns Object with entityType and entityId, or null if invalid
 */
export const parseNamespace = (
  namespace: string | undefined,
): { entityType: string; entityId: string } | null => {
  if (!namespace || typeof namespace !== 'string') {
    return null;
  }

  const colonIndex = namespace.indexOf(':');
  if (colonIndex === -1) {
    return null;
  }

  const entityType = namespace.substring(0, colonIndex);
  const entityId = namespace.substring(colonIndex + 1);

  if (!entityType || !entityId) {
    return null;
  }

  return { entityType, entityId };
};

/**
 * Check if a namespace matches an expected entity type
 *
 * @param namespace - The namespace string from SSE event
 * @param expectedType - The expected entity type (e.g., "section", "rule")
 * @returns The entity ID if matched, null otherwise
 */
export const matchNamespace = (
  namespace: string | undefined,
  expectedType: string,
): string | null => {
  const parsed = parseNamespace(namespace);
  if (parsed && parsed.entityType === expectedType) {
    return parsed.entityId;
  }
  return null;
};

/**
 * Create a namespace string from entity type and ID
 *
 * @param entityType - The entity type (e.g., "section", "rule")
 * @param entityId - The entity ID
 * @returns Formatted namespace string
 */
export const createNamespace = (entityType: string, entityId: string): string => {
  return `${entityType}:${entityId}`;
};

/**
 * SSE performance threshold from environment (default: 10 seconds)
 */
export const getSSEPerformanceThreshold = (): number => {
  return parseInt(import.meta.env.VITE_SSE_PERFORMANCE_THRESHOLD_MS || '10000', 10);
};

